/**
 * Jñāna Kośa content model (B-034-KOSA, docs/MOBILE_APP_SPEC.md §11.4).
 *
 * Articles live as JSON in src/data/kosa/ and are rendered natively by each client:
 * KosaArticle.tsx here, KosaArticleView.swift on iOS (which reads the copy in
 * shared/kosa/, written by `npm run gen:kosa`). The vocabulary is closed: an unknown
 * block type, style or tone is an error, never something a renderer skips.
 *
 * Inline text is a small markup, parsed the same way on every client
 * (shared/kosa/inline_vectors.json holds this parser's answers for the iOS tests):
 *   **bold**   *italic*   `code`   [text](https://…)   [text](kosa:article-id)   [text]{tone}
 *   \x = a literal x
 * Italic may sit inside bold, not the other way round; a single * always closes italic.
 */

import indexJson from '../../data/kosa/index.json';
import adhikaKshayaMasa from '../../data/kosa/articles/adhika-kshaya-masa.json';
import angularSeparation from '../../data/kosa/articles/angular-separation.json';
import ayanamsa from '../../data/kosa/articles/ayanamsa.json';
import dasha from '../../data/kosa/articles/dasha.json';
import divisionalCharts from '../../data/kosa/articles/divisional-charts.json';
import howToHoma from '../../data/kosa/articles/how-to-homa.json';
import howToKundali from '../../data/kosa/articles/how-to-kundali.json';
import howToMuhurtha from '../../data/kosa/articles/how-to-muhurtha.json';
import howToNadiShodhana from '../../data/kosa/articles/how-to-nadi-shodhana.json';
import howToPanchanga from '../../data/kosa/articles/how-to-panchanga.json';
import howToTarabalam from '../../data/kosa/articles/how-to-tarabalam.json';
import kalaVisesha from '../../data/kosa/articles/kala-visesha.json';
import nakshatra from '../../data/kosa/articles/nakshatra.json';
import pranayamaYogaSutra from '../../data/kosa/articles/pranayama-yoga-sutra.json';
import rtu from '../../data/kosa/articles/rtu.json';
import tithi from '../../data/kosa/articles/tithi.json';

// ── Model ────────────────────────────────────────────────────────────

export interface ArticleMeta {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  /** Listed as "Coming Soon", no body yet. */
  wip?: boolean;
  /** In the iOS app's Kośa (spec §11.4: the articles that explain what the app shows). */
  app?: boolean;
}

export type CellTone = 'excellent' | 'very-good' | 'good' | 'fair';
/** A cell: plain inline text, or styled. colspan/rowspan only in a grid (the South Indian chart's centre). */
export type Cell = string | { text: string; medium?: boolean; small?: boolean; tone?: CellTone; colspan?: number; rowspan?: number };

export type Block =
  | { type: 'p'; text: string; space?: 1 | 2 | 3; style?: 'note' | 'verse' | 'label' | 'small' }
  | { type: 'list'; items: string[]; marker: 'disc' | 'decimal' | 'none'; gap: '1' | '1.5' | '2'; indent?: 4; space?: 1 | 2; small?: boolean }
  | { type: 'table'; space: 2 | 3; head?: string[]; rows: { cells: Cell[]; tone?: 'amber' | 'green' }[]; dense?: boolean; grid?: boolean }
  | { type: 'callout'; tone?: 'plain'; space?: 3; blocks: Block[] }
  | { type: 'verse'; original: string; translation: string }
  | { type: 'h4'; text: string }
  | { type: 'group'; blocks: Block[] };

export interface Section { heading?: string; blocks: Block[] }
export interface Article { id: string; sections: Section[] }

export type SpanTone = 'tithi' | 'nakshatra' | 'caution' | 'favourable';
export type InlineNode =
  | { t: 'text'; v: string }
  | { t: 'b' | 'i'; c: InlineNode[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; c: InlineNode[] }
  | { t: 'span'; tone: SpanTone; c: InlineNode[] };

export const SPAN_TONES: readonly SpanTone[] = ['tithi', 'nakshatra', 'caution', 'favourable'];

// ── Inline markup ────────────────────────────────────────────────────

export class KosaMarkupError extends Error {}

/** Parse inline markup into a tree. Throws on anything unclosed or unknown. */
export function parseInline(src: string): InlineNode[] {
  const [nodes, end] = parseRun(src, 0, null);
  if (end !== src.length) throw new KosaMarkupError(`unexpected "${src[end]}" at ${end} in: ${src}`);
  return nodes;
}

function parseRun(s: string, start: number, stop: '**' | '*' | ']' | null): [InlineNode[], number] {
  const out: InlineNode[] = [];
  const text = (v: string) => {
    const last = out[out.length - 1];
    if (last && last.t === 'text') last.v += v;
    else out.push({ t: 'text', v });
  };
  let i = start;
  while (i < s.length) {
    if (stop && s.startsWith(stop, i)) return [out, i];
    const ch = s[i];
    if (ch === '\\') {
      if (i + 1 >= s.length) throw new KosaMarkupError(`trailing backslash in: ${s}`);
      text(s[i + 1]);
      i += 2;
    } else if (s.startsWith('**', i)) {
      const [c, end] = parseRun(s, i + 2, '**');
      if (!s.startsWith('**', end)) throw new KosaMarkupError(`unclosed ** in: ${s}`);
      out.push({ t: 'b', c });
      i = end + 2;
    } else if (ch === '*') {
      const [c, end] = parseRun(s, i + 1, '*');
      if (s[end] !== '*') throw new KosaMarkupError(`unclosed * in: ${s}`);
      out.push({ t: 'i', c });
      i = end + 1;
    } else if (ch === '`') {
      const end = s.indexOf('`', i + 1);
      if (end < 0) throw new KosaMarkupError(`unclosed \` in: ${s}`);
      out.push({ t: 'code', v: s.slice(i + 1, end) });
      i = end + 1;
    } else if (ch === '[') {
      const [c, end] = parseRun(s, i + 1, ']');
      if (s[end] !== ']') throw new KosaMarkupError(`unclosed [ in: ${s}`);
      const open = s[end + 1];
      const close = open === '(' ? ')' : open === '{' ? '}' : null;
      const stopAt = close ? s.indexOf(close, end + 2) : -1;
      if (stopAt < 0) throw new KosaMarkupError(`[text] needs (href) or {tone} in: ${s}`);
      const arg = s.slice(end + 2, stopAt);
      if (open === '(') {
        if (!/^https:\/\/\S+$/.test(arg) && !/^kosa:[a-z0-9-]+$/.test(arg)) {
          throw new KosaMarkupError(`a link is https://… or kosa:<article id>, not: ${arg}`);
        }
        out.push({ t: 'link', href: arg, c });
      } else {
        if (!SPAN_TONES.includes(arg as SpanTone)) throw new KosaMarkupError(`unknown tone {${arg}} in: ${s}`);
        out.push({ t: 'span', tone: arg as SpanTone, c });
      }
      i = stopAt + 1;
    } else if (ch === ']') {
      throw new KosaMarkupError(`stray ] in: ${s}`);
    } else {
      text(ch);
      i += 1;
    }
  }
  if (stop) throw new KosaMarkupError(`unclosed ${stop === ']' ? '[' : stop} in: ${s}`);
  return [out, i];
}

// ── Validation (closed vocabulary) ───────────────────────────────────

const P_STYLES = ['note', 'verse', 'label', 'small'];
const CELL_TONES = ['excellent', 'very-good', 'good', 'fair'];

/** Every inline string in a block tree, in reading order. */
export function inlineStrings(blocks: Block[]): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    switch (b.type) {
      case 'p': case 'h4': out.push(b.text); break;
      case 'list': out.push(...b.items); break;
      case 'table':
        out.push(...(b.head ?? []));
        for (const r of b.rows) for (const c of r.cells) out.push(typeof c === 'string' ? c : c.text);
        break;
      case 'callout': case 'group': out.push(...inlineStrings(b.blocks)); break;
      case 'verse': out.push(b.original, b.translation); break;
    }
  }
  return out;
}

/** The articles an article links to (kosa:<id>), in reading order. */
export function articleLinks(a: Article): string[] {
  const out: string[] = [];
  const walk = (ns: InlineNode[]) => ns.forEach((n) => {
    if (n.t === 'link' && n.href.startsWith('kosa:')) out.push(n.href.slice(5));
    if ('c' in n) walk(n.c);
  });
  for (const s of a.sections) for (const t of [s.heading ?? '', ...inlineStrings(s.blocks)]) walk(parseInline(t));
  return out;
}

/** Problems in one article, as readable strings; empty when it is valid. */
export function validateArticle(a: Article): string[] {
  const errs: string[] = [];
  const at = (path: string, msg: string) => errs.push(`${a.id} ${path}: ${msg}`);
  const allowed = (path: string, obj: object, keys: string[]) => {
    for (const k of Object.keys(obj)) if (!keys.includes(k)) at(path, `unknown field "${k}"`);
  };
  const blocks = (list: Block[], path: string, inCallout: boolean) => list.forEach((b, n) => {
    const p = `${path}[${n}]`;
    switch (b.type) {
      case 'p':
        allowed(p, b, ['type', 'text', 'space', 'style']);
        if (b.style && !P_STYLES.includes(b.style)) at(p, `unknown style ${b.style}`);
        if (b.space !== undefined && ![1, 2, 3].includes(b.space)) at(p, `space ${b.space}`);
        break;
      case 'list':
        allowed(p, b, ['type', 'items', 'marker', 'gap', 'indent', 'space', 'small']);
        if (!['disc', 'decimal', 'none'].includes(b.marker)) at(p, `marker ${b.marker}`);
        if (!['1', '1.5', '2'].includes(b.gap)) at(p, `gap ${b.gap}`);
        if (b.indent !== undefined && (b.indent !== 4 || b.marker === 'none')) at(p, `indent ${b.indent}`);
        if (b.space !== undefined && ![1, 2].includes(b.space)) at(p, `space ${b.space}`);
        if (!b.items.length) at(p, 'empty list');
        break;
      case 'table':
        allowed(p, b, ['type', 'space', 'head', 'rows', 'dense', 'grid']);
        if (![2, 3].includes(b.space)) at(p, `space ${b.space}`);
        b.rows.forEach((r, ri) => {
          allowed(`${p}.rows[${ri}]`, r, ['cells', 'tone']);
          if (r.tone && !['amber', 'green'].includes(r.tone)) at(p, `row tone ${r.tone}`);
          if (b.head && r.cells.length !== b.head.length) at(p, `row ${ri} has ${r.cells.length} cells, head has ${b.head.length}`);
          for (const c of r.cells) if (typeof c !== 'string') {
            allowed(`${p}.rows[${ri}]`, c, b.grid ? ['text', 'colspan', 'rowspan'] : ['text', 'medium', 'small', 'tone']);
            if (c.tone && !CELL_TONES.includes(c.tone)) at(p, `cell tone ${c.tone}`);
          }
        });
        break;
      case 'callout':
        allowed(p, b, ['type', 'tone', 'space', 'blocks']);
        if (b.tone !== undefined && b.tone !== 'plain') at(p, `tone ${b.tone}`);
        blocks(b.blocks, `${p}.blocks`, true);
        break;
      case 'verse': allowed(p, b, ['type', 'original', 'translation']); break;
      case 'h4':
        allowed(p, b, ['type', 'text']);
        if (!inCallout) at(p, 'h4 belongs in a callout');
        break;
      case 'group':
        allowed(p, b, ['type', 'blocks']);
        if (b.blocks.some((x) => x.type !== 'callout')) at(p, 'a group holds callouts only');
        blocks(b.blocks, `${p}.blocks`, false);
        break;
      default:
        at(p, `unknown block type ${(b as { type: string }).type}`);
    }
  });
  a.sections.forEach((s, n) => {
    allowed(`sections[${n}]`, s, ['heading', 'blocks']);
    blocks(s.blocks, `sections[${n}]`, false);
    for (const t of [s.heading ?? '', ...inlineStrings(s.blocks)]) {
      try { parseInline(t); } catch (e) { at(`sections[${n}]`, (e as Error).message); }
    }
  });
  return errs;
}

// ── Content ──────────────────────────────────────────────────────────

export const KOSA_INDEX: ArticleMeta[] = indexJson.articles;

/** Bodies by id. A non-WIP index entry without one fails kosa.test.ts. */
export const KOSA_ARTICLES: Record<string, Article> = Object.fromEntries(
  ([
    adhikaKshayaMasa, angularSeparation, ayanamsa, dasha, divisionalCharts, howToHoma, howToKundali,
    howToMuhurtha, howToNadiShodhana, howToPanchanga, howToTarabalam, kalaVisesha, nakshatra,
    pranayamaYogaSutra, rtu, tithi,
  ] as unknown as Article[]).map((a) => [a.id, a]),
);
